import { useEffect } from 'react';
import useAuthStore from '../store/authStore';
import { apiPost } from '../utils/api';

const GoogleOneTap = () => {
	const { user, setUser } = useAuthStore();

	useEffect(() => {
		if (user) return; // Only show when logged out
		if (typeof window === 'undefined' || !window.google || !window.google.accounts?.id) return;

		// Suppress FedCM warnings globally
		const originalConsoleWarn = console.warn;
		console.warn = (...args) => {
			const message = args[0];
			if (typeof message === 'string' && message.includes('FedCM') && message.includes('migration')) {
				// Suppress FedCM migration warnings
				return;
			}
			if (typeof message === 'string' && message.includes('GSI_LOGGER') && message.includes('FedCM')) {
				// Suppress GSI FedCM warnings
				return;
			}
			originalConsoleWarn.apply(console, args);
		};

		// Check if we're in a supported environment
		const currentOrigin = window.location.origin;
		const isLocalhost = currentOrigin.includes('localhost') || currentOrigin.includes('127.0.0.1');
		const isProduction = currentOrigin.includes('ytproud.com') || currentOrigin.includes('yourdomain.com');
		
		// Only initialize if we have a valid client ID
		if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
			return;
		}
		
		// Skip initialization if origin is not properly configured
		if (!isLocalhost && !isProduction) {
			return;
		}

		// For localhost, check if the specific port is likely configured
		if (isLocalhost) {
			const port = window.location.port;
			if (port && port !== '3000' && port !== '5000') {
				// Don't return here, just warn - let it try anyway
			}
		}
		
		// Additional safety check: if we've seen origin errors before, disable
		const hasOriginError = sessionStorage.getItem('google_origin_error');
		if (hasOriginError) {
			return;
		}
		
		// Clear origin error if user is logged in (fresh session)
		if (user) {
			sessionStorage.removeItem('google_origin_error');
		}

		try {
			window.google.accounts.id.initialize({
				client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
				callback: async ({ credential }) => {
					try {
						// 1) Verify ID token to get profile
						const verifyRes = await apiPost('/api/auth/google/verify', { idToken: credential });
						if (!verifyRes.ok) return;
						const verifyData = await verifyRes.json();
						const g = verifyData.user;

						// 2) Create/login session on backend
						const authRes = await apiPost('/api/auth/google', {
							email: g.email,
							name: g.name,
							picture: g.picture,
							googleId: g.googleId,
							provider: 'google'
						});
						if (authRes.ok) {
							const result = await authRes.json();
							setUser(result.user);
						}
					} catch (err) {
					}
				},
				auto_select: false,
				cancel_on_tap_outside: false,
				context: 'signin',
				itp_support: true,
				// FedCM migration: Use new prompt methods
				use_fedcm_for_prompt: true,
				// Add FedCM-specific configuration
				fedcm_mode: 'auto',
				// Additional FedCM settings
				state_cookie_domain: window.location.hostname,
				ux_mode: 'popup'
			});

			window.google.accounts.id.prompt((notification) => {
				try {
					// FedCM-compatible notification handling
					if (notification.isDisplayed()) {
					} else if (notification.isNotDisplayed()) {
						const reason = notification.getNotDisplayedReason();
						// Track origin errors to prevent future attempts
						if (reason === 'unregistered_origin') {
							sessionStorage.setItem('google_origin_error', 'true');
						}
						// Don't log warnings for expected reasons
						if (reason !== 'suppressed_by_user' && reason !== 'cancel_called' && reason !== 'unregistered_origin') {
						}
					} else if (notification.isSkippedMoment()) {
						const reason = notification.getSkippedReason();
						// FedCM migration: Use new skipped moment handling
						if (reason === 'auto_cancel' || reason === 'user_cancel') {
							// Expected behavior, don't warn
						} else {
						}
					} else if (notification.isDismissedMoment()) {
						const reason = notification.getDismissedReason();
					}
				} catch (error) {
				}
			});
		} catch (error) {
		}

		// Restore original console.warn on cleanup
		return () => {
			console.warn = originalConsoleWarn;
		};
	}, [user]);

	return null;
};

export default GoogleOneTap;
